import { getCompactSelectStyles } from '@databases/atlaskit/compact-select-styles';
import Select, { type StylesConfig } from '@databases/atlaskit/Select';
import { messages as intlMessages } from '@databases/intl/messages';
import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { type ValueChange } from '@databases/utils/database/changesets';
import React, { type FunctionComponent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

type CheckboxDefaultValueSelectOption = {
  label: string;
  value: boolean;
};

type CheckboxDefaultValueSelectProps = {
  ariaDescribedBy?: string;
  ariaLabel: string;
  inputId: string;
  value: boolean;
  onChange: (
    value: DatabaseValue<DatabaseFieldType.CHECKBOX>,
    change: ValueChange
  ) => void;
};

const checkboxDefaultValueSelectStyles: StylesConfig<
  CheckboxDefaultValueSelectOption,
  false
> = {
  ...getCompactSelectStyles<CheckboxDefaultValueSelectOption, false>(),
  menuPortal: (base) => ({
    ...base,
    zIndex: 750
  })
};

const CheckboxDefaultValueSelect: FunctionComponent<CheckboxDefaultValueSelectProps> = ({
  ariaDescribedBy,
  ariaLabel,
  inputId,
  value,
  onChange
}) => {
  const { formatMessage } = useIntl();
  const options = useMemo<CheckboxDefaultValueSelectOption[]>(
    () => [
      {
        label: formatMessage(intlMessages['checkbox-filter.unchecked']),
        value: false
      },
      {
        label: formatMessage(intlMessages['checkbox-filter.checked']),
        value: true
      }
    ],
    [formatMessage]
  );
  const menuPortalTarget =
    typeof globalThis.document === 'undefined' ? undefined : globalThis.document.body;

  const handleOptionChange = useCallback(
    (option: CheckboxDefaultValueSelectOption | null) => {
      if (!option) {
        return;
      }

      const value = {
        type: DatabaseFieldType.CHECKBOX,
        value: option.value
      } as DatabaseValue<DatabaseFieldType.CHECKBOX>;

      onChange(value, value);
    },
    [onChange]
  );

  return (
    <Select<CheckboxDefaultValueSelectOption, false>
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      inputId={inputId}
      menuPortalTarget={menuPortalTarget}
      menuPosition="fixed"
      options={options}
      spacing="compact"
      styles={checkboxDefaultValueSelectStyles}
      value={options.find((option) => option.value === value) ?? null}
      onChange={handleOptionChange}
    />
  );
};

export default CheckboxDefaultValueSelect;
